import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import Dialog from '@/storybook/components/Dialog/Dialog';
import Text from '@/storybook/components/Text/Text';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './DeleteConversationDialog.module.css';

interface DeleteConversationDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  conversationTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const MAX_TITLE_LENGTH = 120;

const truncateTitle = (title: string): string =>
  title.length > MAX_TITLE_LENGTH ? `${title.slice(0, MAX_TITLE_LENGTH)}…` : title;

const DeleteConversationDialog: FC<DeleteConversationDialogProps> = ({
  isOpen,
  isLoading,
  conversationTitle,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  const footer = (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={isLoading}
        data-testid={CHAT_HISTORY_TEST_IDS.deleteDialogCancel}
      >
        {t('components.aiChatDrawer.history.deleteDialog.cancel')}
      </Button>
      <Button
        variant="primary"
        color="warning"
        onClick={onConfirm}
        disabled={isLoading}
        loading={isLoading}
        data-testid={CHAT_HISTORY_TEST_IDS.deleteDialogConfirm}
      >
        {t('components.aiChatDrawer.history.deleteDialog.confirm')}
      </Button>
    </>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={t('components.aiChatDrawer.history.deleteDialog.title')}
      size="S"
      className={styles.dialog}
      footer={footer}
      data-testid={CHAT_HISTORY_TEST_IDS.deleteDialog}
    >
      <Text as="p" className={styles.message}>
        <Trans
          i18nKey="components.aiChatDrawer.history.deleteDialog.message"
          values={{ title: truncateTitle(conversationTitle) }}
          components={{ bold: <strong /> }}
        />
      </Text>
    </Dialog>
  );
};

export default DeleteConversationDialog;
